import axios, { AxiosHeaders, AxiosPromise, AxiosRequestConfig, AxiosResponse, CancelTokenSource, Method } from 'axios';
import qs from 'qs';

import { Collection, PathParam, RouteApi } from '#app/typings/api';

const requests: Record<string, CancelTokenSource> = {};
const cancelToken = axios?.CancelToken;

const instance = axios.create({
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

export function buildQueryParams(params: Record<string, any>): string {
  if (!params || Object.keys(params).length === 0) {
    return '';
  }

  const cleanedParams: Record<string, any> = {};

  for (const [key, value] of Object.entries(params)) {
    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
    ) {
      continue;
    }

    cleanedParams[key] = value;
  }

  return qs.stringify(cleanedParams, { arrayFormat: 'brackets' });
}

export function generateApiPath<Path extends RouteApi>(
  originalPath: Path,
  params: {
    [key in PathParam<Path>]: string | null;
  } = {} as any,
  filters: object = {},
): string {
  const path: string = originalPath;
  const prefix = path.startsWith('/') ? '/' : '';

  const stringify = (p: any): string => (p == null ? '' : typeof p === 'string' ? p : String(p));

  const segments = path
    .split(/\/+/)
    .map((segment, index, array) => {
      const isLastSegment = index === array.length - 1;

      if (isLastSegment && segment === '*') {
        const star = '*' as PathParam<Path>;
        return stringify(params[star]);
      }

      const keyMatch = segment.match(/^\{([\w-]+)(\??)}$/);
      if (keyMatch) {
        const [, key] = keyMatch;
        const param = params[key as PathParam<Path>];

        if (param === null) {
          throw new Error(`Missing "{${key}}" param`);
        }
        return stringify(param);
      }

      return segment;
    })
    .filter((segment) => !!segment);

  const searchParams = buildQueryParams(filters);
  return prefix + segments.join('/') + ('' !== searchParams ? '?' + searchParams : '');
}

export function requestAPI<T>(): (options: AxiosRequestConfig) => AxiosPromise<T> {
  return async (options: AxiosRequestConfig) => {
    options.headers = options.headers || new AxiosHeaders();

    const token = `${options.method}-${(options.url ?? '').split('?')[0]}-${JSON.stringify(options.data ?? {})}`;

    try {
      const source = cancelToken?.source();

      if (requests[token]) {
        requests[token]?.cancel();
      }
      requests[token] = source;

      const axiosResponse: AxiosResponse<T> = await instance.request<T>({
        ...options,
        cancelToken: source?.token,
      });
      delete requests[token];

      return axiosResponse;
    } catch (exception) {
      delete requests[token];

      throw exception;
    }
  };
}

export function writeItem<T>(method: Method): (
  url: string,
  data: {
    arg?: any;
  },
) => Promise<T> {
  return async (
    url: string,
    {
      arg,
    }: {
      arg?: any;
    },
  ): Promise<T> => {
    const { data: responseData }: AxiosResponse<T> = await requestAPI<T>()({
      url,
      method,
      data: arg,
    });
    return responseData;
  };
}

export async function getItem<T>(url: string): Promise<T> {
  const { data }: AxiosResponse<{ data: T }> = await requestAPI<{ data: T }>()({ url });
  return data.data;
}
export async function getCollection<T>(url: string): Promise<T[]> {
  const { data }: AxiosResponse<{ data: T[] }> = await requestAPI<{ data: T[] }>()({ url });
  return data.data;
}

export async function getCollectionPaginated<T>(url: string): Promise<Collection<T>> {
  const { data }: AxiosResponse<Collection<T>> = await requestAPI<Collection<T>>()({ url });
  return data;
}
